import { useEffect, useState } from 'react';
import { useQuery } from '@apollo/client';
import { Alert, Card, Col, Container, Row } from 'react-bootstrap';
import { useHistory, useParams } from 'react-router';

import { GachaShopConfigDataFragment, GetGachaCampaignsQuery } from '../../../../../__gqltypes__';
import { LoadingLogo } from '../../../devtools/components/Modal';
import { GachaCampaignEditor } from './components/GachaCampaignEditor';
import { GachaCampaignList } from './components/GachaCampaignList';
import { GachaKillSwitch } from './components/GachaKillSwitch';
import { GET_GACHA_CAMPAIGNS } from './graphql';
import { GACHA_BASE_URL, GachaCampaignData } from './utils';

/**
 * Gacha Shop (Surprise Boxes): the feature kill switch, the campaign list in carousel order and the
 * selected campaign's editor. Several campaigns run at once; each holds one or more boxes.
 */
export default function GachaShop(): JSX.Element {
  const { id: routeId } = useParams<{ id?: string }>();
  const history = useHistory();
  const selectedId = routeId && routeId !== ':id' ? routeId : null;

  // Loaded once; afterwards every mutation result updates the local state (no refetch, so an open editor never
  // loses its unsaved changes to a list reload).
  const { data, loading, error } = useQuery<GetGachaCampaignsQuery>(GET_GACHA_CAMPAIGNS, {
    fetchPolicy: 'no-cache',
  });
  const [campaigns, setCampaigns] = useState<GachaCampaignData[]>([]);
  const [shopConfig, setShopConfig] = useState<GachaShopConfigDataFragment | null>(null);
  // Campaigns created in this session: the editor opens their matcher sections expanded.
  const [createdIds, setCreatedIds] = useState<ReadonlySet<string>>(() => new Set());

  useEffect(() => {
    if (data?.liveOps) {
      setCampaigns(data.liveOps.gachaCampaigns);
      setShopConfig(data.liveOps.gachaShopConfig);
    }
  }, [data]);

  const selected = campaigns.find((campaign) => campaign.id === selectedId) ?? null;

  const replaceCampaign = (updated: GachaCampaignData) =>
    setCampaigns((previous) => previous.map((campaign) => (campaign.id === updated.id ? updated : campaign)));

  const selectCampaign = (id: string | null) => history.push(id ? `${GACHA_BASE_URL}/${id}` : GACHA_BASE_URL);

  return (
    <Container fluid className="pb-4">
      <LoadingLogo show={loading && !data} />
      <GachaKillSwitch config={shopConfig} onChanged={setShopConfig} />
      {error && <Alert variant="danger">Failed to load the campaigns: {error.message}</Alert>}
      <Row>
        <Col md={3} style={{ maxHeight: 'calc(100vh - 12rem)', overflowY: 'auto' }}>
          <GachaCampaignList
            campaigns={campaigns}
            selectedId={selectedId}
            pinnedCampaignId={shopConfig?.pinnedCampaignId ?? null}
            onSelect={selectCampaign}
            onCampaignsChange={setCampaigns}
            onCreated={(campaign) => {
              setCreatedIds((previous) => new Set(previous).add(campaign.id));
              setCampaigns((previous) => [...previous, campaign]);
              selectCampaign(campaign.id);
            }}
            onDeleted={(id) => {
              setCampaigns((previous) => previous.filter((campaign) => campaign.id !== id));
              if (id === selectedId) {
                selectCampaign(null);
              }
              // The server clears the pin when it pointed into the deleted campaign.
              setShopConfig((previous) =>
                previous && previous.pinnedCampaignId === id
                  ? { ...previous, pinnedCampaignId: null, pinnedBoxId: null }
                  : previous
              );
            }}
          />
        </Col>
        <Col md={9}>
          {selected ? (
            <GachaCampaignEditor
              // A save or a newer server version remounts the editor from the server state.
              key={`${selected.id}:${selected.configVersion}`}
              campaign={selected}
              isNew={createdIds.has(selected.id)}
              onCampaignUpdated={replaceCampaign}
              allCampaigns={campaigns}
              shopConfig={shopConfig}
              onShopConfigChange={setShopConfig}
              onCloned={(clone) => {
                setCampaigns((previous) => [...previous, clone]);
                selectCampaign(clone.id);
              }}
            />
          ) : (
            <Card>
              <Card.Body className="text-muted">
                {selectedId && !loading
                  ? 'This campaign does not exist (it may have been deleted).'
                  : 'Select a campaign, or create one.'}
              </Card.Body>
            </Card>
          )}
        </Col>
      </Row>
    </Container>
  );
}
